// SavedView persistence. Пока backend endpoint `/me/saved-views` не готов —
// используем localStorage fallback. После S8 — переезд на server-side.
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

export const SavedView = z.object({
  id: z.string(),
  name: z.string(),
  resource: z.string(),
  filters: z.record(z.unknown()),
  is_shared: z.boolean().default(false),
  created_at: z.string().datetime(),
});
export type SavedView = z.infer<typeof SavedView>;

const KEY = 'staiter.saved_views.v1';

function readAll(): SavedView[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = z.array(SavedView).safeParse(JSON.parse(raw));
    return arr.success ? arr.data : [];
  } catch {
    return [];
  }
}

function writeAll(views: SavedView[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(views));
  } catch {
    /* ignore */
  }
}

export function useSavedViews(resource: string) {
  const [views, setViews] = useState<SavedView[]>(() =>
    readAll().filter((v) => v.resource === resource),
  );

  const save = useCallback(
    (name: string, filters: Record<string, unknown>) => {
      const next: SavedView = {
        id: crypto.randomUUID(),
        name,
        resource,
        filters,
        is_shared: false,
        created_at: new Date().toISOString(),
      };
      const all = [...readAll(), next];
      writeAll(all);
      setViews(all.filter((v) => v.resource === resource));
      return next;
    },
    [resource],
  );

  const remove = useCallback(
    (id: string) => {
      const all = readAll().filter((v) => v.id !== id);
      writeAll(all);
      setViews(all.filter((v) => v.resource === resource));
    },
    [resource],
  );

  useEffect(() => {
    setViews(readAll().filter((v) => v.resource === resource));
  }, [resource]);

  return { views, save, remove };
}
